import React from "react";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface ExtensionConfirmationDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    config: any;
    onConfirm?: () => void;
}

export function ExtensionConfirmationDialog({ open, onOpenChange, config, onConfirm }: ExtensionConfirmationDialogProps) {
    if (!config) return null;

    const url = config.downloadUrl || config.zipUrl;
    const isZip = !!config.zipUrl && !config.downloadUrl;

    const [downloading, setDownloading] = React.useState(false);

    const handleAction = async (e: React.MouseEvent) => {
        if (!url) {
            onOpenChange(false);
            return;
        }

        if (isZip) {
            e.preventDefault(); // Prevent default close if we want to show loading
            setDownloading(true);
            try {
                console.log("[DownloadDebug] Fetching URL:", url);
                // Fetch blob to enforce download and avoid "new tab" behavior for cross-origin
                const response = await fetch(url);
                if (!response.ok) throw new Error(`Download failed with status: ${response.status}`);

                const contentType = response.headers.get("content-type");
                console.log("[DownloadDebug] Content-Type:", contentType);
                if (contentType && contentType.includes("text/html")) {
                    throw new Error("Invalid file format: Detected HTML instead of ZIP. The file might be missing.");
                }

                const blob = await response.blob();
                const blobUrl = window.URL.createObjectURL(blob);

                // Create temporary link
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = "traskr-extension-latest.zip";
                document.body.appendChild(link);
                link.click();

                // Cleanup
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);

                if (onConfirm) onConfirm();
                onOpenChange(false);
            } catch (err: any) {
                console.error("Download failed:", err);

                // If it was our specific HTML check, alert the user instead of falling back
                if (err.message && err.message.includes("Invalid file format")) {
                    alert("Download Error: The extension file was not found (404). Please contact support.");
                    setDownloading(false);
                    return;
                }

                // Fallback (e.g. if CORS fails)
                // Only fallback if URL looks like a remote URL, not relative
                if (url.startsWith('http')) {
                    window.open(url, '_blank');
                } else {
                    alert("Download Error: File not found.");
                }
                onOpenChange(false);
            } finally {
                setDownloading(false);
            }
        } else {
            // Chrome Store Link
            window.open(url, '_blank');
            if (onConfirm) onConfirm();
            onOpenChange(false);
        }
    };

    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>
                        {isZip ? "Download Extension?" : "Visit Chrome Web Store"}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                        {isZip
                            ? "Do you want to download the extension files? You will need to load them manually in Developer Mode."
                            : "Navigating to the Chrome Web Store to install the Traskr Companion extension."}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel disabled={downloading}>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleAction} disabled={downloading}>
                        {downloading ? "Downloading..." : (isZip ? "Download ZIP" : "Go to Store")}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
